import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as GiIcons from 'react-icons/gi';
import SafeIcon from '../../../common/SafeIcon';

const { FiAward } = FiIcons;
const { GiSushis, GiKatana } = GiIcons;

const principles = [
  {
    icon: GiSushis,
    title: '修行之道',
    titleJp: '修行の道',
    subtitle: 'Path of Practice',
    description: '多年的訓練與實踐，從學徒到師傅，每一步都是對技藝的淬煉與提升。',
    quote: '十年磨一劍，方能成大器',
    gradient: 'from-blue-500 to-indigo-500'
  },
  {
    icon: GiKatana,
    title: '刀工精髓',
    titleJp: '包丁の極意',
    subtitle: 'Essence of Knife Skills',
    description: '精準的刀工是日本料理的基礎，每一刀都體現著廚師的功力與對食材的理解。',
    quote: '刀隨心動，心隨刀行',
    gradient: 'from-rose-500 to-purple-500'
  },
  {
    icon: FiAward,
    title: '平衡藝術',
    titleJp: 'バランスの芸術',
    subtitle: 'Art of Balance',
    description: '味道、質感、視覺的完美平衡，創造出令人難忘的用餐體驗。',
    quote: '五味調和，萬物生香',
    gradient: 'from-amber-500 to-orange-500'
  }
];

function CraftsmanshipSection() {
  return (
    <section id="craftsmanship" className="py-24 relative overflow-hidden">
      <div className="container-custom relative z-10">
        {/* 🚀 優化：減少動畫時間 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            匠人精神的體現
            <span className="block text-2xl md:text-3xl mt-2 text-rose-500 dark:text-rose-400 font-normal">
              職人精神の体現
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            日本料理中的職人文化與技藝傳承
          </p>
        </motion.div>

        <div className="space-y-12">
          {principles.map((principle, index) => (
            <motion.div
              key={principle.title}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}
            >
              {/* Icon Side */}
              <div className="flex-1 flex justify-center">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className={`w-32 h-32 rounded-3xl bg-gradient-to-br ${principle.gradient} p-8 shadow-2xl`}
                >
                  <SafeIcon icon={principle.icon} className="w-full h-full text-white" />
                </motion.div>
              </div>

              {/* Content Side */}
              <div className="flex-1">
                <motion.div
                  whileHover={{ y: -5 }}
                  className="card p-8 relative group"
                >
                  <motion.div
                    className={`absolute inset-0 rounded-xl bg-gradient-to-br ${principle.gradient} opacity-0 group-hover:opacity-5 transition-opacity -z-10`}
                  />
                  
                  <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-1">
                    {principle.title}
                  </h3>
                  <p className="text-xl text-slate-500 dark:text-slate-400 mb-2">
                    {principle.titleJp}
                  </p>
                  <p className={`text-rose-500 dark:text-rose-400 font-medium mb-4 bg-gradient-to-r ${principle.gradient} bg-clip-text group-hover:text-transparent transition-all`}>
                    {principle.subtitle}
                  </p>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
                    {principle.description}
                  </p>
                  <blockquote className="pl-4 italic text-slate-500 dark:text-slate-400">
                    {principle.quote}
                  </blockquote>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default CraftsmanshipSection;